"use client";

import RotatingCTA from "@/components/ui/RotatingCTA";
import { ArrowUpRight, Copy } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { FaInstagram, FaLinkedin } from "react-icons/fa6";
import DontPressButton from "./DontPressButton";

interface FooterProps {
    isGlobal?: boolean;
}

export default function Footer({ isGlobal = false }: FooterProps) {
    const pathname = usePathname();

    if (isGlobal && pathname?.startsWith("/ai-validator/result")) {
        return null;
    }

    const currentYear = new Date().getFullYear();

    return (
        <footer className="relative pt-20 pb-0 overflow-hidden">
            {/* Glass Background Layer */}
            <div className="absolute inset-0 bg-white/30 backdrop-blur-3xl backdrop-saturate-150 z-[-1]" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent z-[-1]" />

            {/* Top Border */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-10 opacity-70" />

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-b border-gray-200/50">

                    {/* Brand & Stats Column - 4 cols */}
                    <div className="lg:col-span-4 lg:border-r border-gray-200/50 flex flex-col justify-between p-8 lg:pl-0 lg:py-8 min-h-[400px]">
                        <div>
                            <Link href="/" className="flex items-center space-x-3 mb-8 group w-fit">
                                <div className="relative w-10 h-10 flex items-center justify-center overflow-hidden transition-all">
                                    <Image src="/logo-black-64.png" alt="DharmaTech Logo" width={40} height={40} className="w-10 h-10 object-contain opacity-80 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <span className="text-2xl font-heading tracking-tight">
                                    <span className="font-semibold text-zinc-900">Dharma</span>
                                    <span className="font-light text-zinc-500">Tech</span>
                                </span>
                            </Link>
                            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest mb-4"> // Engineering The Future</p>
                            <h2 className="text-3xl font-medium tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-black to-gray-600 max-w-sm">
                                Crafting digital excellence with precision engineering.
                            </h2>
                        </div>

                        <div className="mt-12">
                            <div className="flex items-center space-x-2 text-xs font-mono text-gray-400 uppercase tracking-widest mb-4">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                <span>Systems Operational</span>
                            </div>
                            <p className="text-sm text-gray-500 max-w-xs">
                                Global Headquarters<br />
                                Pran Prabha, Nanpura<br />
                                Surat, India
                            </p>
                        </div>
                    </div>

                    {/* Navigation Grid - 5 cols */}
                    <div className="lg:col-span-5 flex flex-col">
                        <div className="grid grid-cols-2 h-full">
                            {/* Services */}
                            <div className="border-r border-gray-200/50 p-8 flex flex-col">
                                <h3 className="font-mono text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">01 / Superpowers</h3>
                                <ul className="space-y-4 flex-1">
                                    <TechLink href="/services">Web Development</TechLink>
                                    <TechLink href="/services">Mobile Engineering</TechLink>
                                    <TechLink href="/services">UI/UX Architecture</TechLink>
                                    <TechLink href="/services">Cloud Systems</TechLink>
                                </ul>
                            </div>

                            {/* Company */}
                            <div className="p-8 flex flex-col">
                                <h3 className="font-mono text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">02 / Company</h3>
                                <ul className="space-y-4 flex-1">
                                    <TechLink href="/about">About & Vision</TechLink>
                                    <TechLink href="/careers">Join the Team</TechLink>
                                    <TechLink href="/case-studies">Selected Work</TechLink>
                                    <TechLink href="/blog">Insights</TechLink>
                                </ul>
                            </div>
                        </div>
                        {/* Bottom Row inside Navigation Column */}
                        <div className="border-t border-gray-200/50 p-8">
                            <h3 className="font-mono text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">03 / Connect</h3>
                            <div className="flex gap-4">
                                <SocialButton icon={FaLinkedin} href="https://www.linkedin.com/company/dharma-tech" label="LinkedIn" />
                                <SocialButton icon={FaInstagram} href="https://www.instagram.com/dharmatech.in/" label="Instagram" />
                            </div>
                        </div>
                    </div>

                    {/* CTA Column - 3 cols */}
                    <div className="lg:col-span-3 lg:border-l border-gray-200/50 p-8 flex flex-col justify-between bg-white/40 backdrop-blur-sm">
                        <div className="space-y-2">
                            <h3 className="font-mono text-xs font-bold text-gray-400 uppercase tracking-widest">Start a Project</h3>
                            <div className="h-px w-10 bg-black/20"></div>
                        </div>

                        <div className="py-12">
                            <h3 className="text-2xl font-semibold mb-6">Have an ambitious idea?</h3>
                            <Link
                                href="/contact"
                                className="group w-full flex items-center justify-between p-4 bg-black text-white rounded-lg hover:bg-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
                            >
                                <RotatingCTA className="font-medium" />
                                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </Link>
                        </div>

                        <div className="bg-white/50 rounded-lg p-4 border border-white/60 backdrop-blur-md">
                            <p className="font-mono text-xs text-gray-500 mb-2">Direct Contact</p>
                            <CopyToClipboard text="contact@dharmatech.in" />
                        </div>
                    </div>
                </div>

                {/* Footer Bottom */}
                <div className="flex flex-col md:flex-row justify-between items-center py-8 text-xs text-gray-500 font-mono tracking-wide">
                    <p>&copy; {currentYear} DharmaTech Pvt. Ltd.</p>
                    <div className="flex items-center space-x-6 mt-4 md:mt-0">
                        <Link href="/privacy" className="hover:text-black transition-colors">PRIVACY</Link>
                        <Link href="/terms" className="hover:text-black transition-colors">TERMS</Link>
                        <div className="pl-6 border-l border-gray-300">
                            <DontPressButton />
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function TechLink({ href, children }: { href: string, children: React.ReactNode }) {
    return (
        <li className="block">
            <Link href={href} className="group flex items-center justify-between text-sm text-gray-600 hover:text-black transition-all">
                <span>{children}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-black/0 group-hover:bg-black/100 transition-all duration-300"></span>
            </Link>
        </li>
    );
}

function SocialButton({ icon: Icon, href, label }: { icon: any, href: string, label: string }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/50 border border-gray-200 text-gray-500 hover:text-black hover:border-black/30 hover:bg-white transition-all duration-300"
            aria-label={label}
        >
            <Icon className="w-4 h-4" />
        </a>
    )
}

function CopyToClipboard({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="w-full flex items-center justify-between group cursor-copy"
        >
            <span className="text-sm font-medium text-black truncate">{text}</span>
            <span className="p-1 rounded-md bg-gray-100 group-hover:bg-gray-200 transition-colors">
                {copied ? <span className="text-[10px] font-bold px-1">COPIED</span> : <Copy className="w-3.5 h-3.5 text-gray-500" />}
            </span>
        </button>
    )
}
